import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import AnalyticsService from '../../services/AnalyticsService';

const EMPTY_PROXY = { protocol: 'http', host: '', port: '', username: '' };
const POLL_MS = 5000;

function connectionSummary(connection) {
  if (connection.needsReconnect)
    return { severity: 'warning', text: 'Authorization expired or was revoked. Create a new sign-in link to reconnect.' };
  if (!connection.connected)
    return { severity: 'info', text: 'Not connected yet. Save the proxy, then sign in with Google using the link below.' };
  if (!connection.revenueGranted)
    return {
      severity: 'warning',
      text: `Connected to ${connection.youtubeChannelTitle} without revenue access. Reconnect and approve revenue access to include revenue.`,
    };
  return { severity: 'success', text: `Connected to ${connection.youtubeChannelTitle}.` };
}

/**
 * Connects a YouTube channel to Analytics: save the channel's proxy, then sign
 * in with Google through that proxy. With no `channel`, adds a new channel.
 */
export default function ConnectionDialog({ channel, onClose, onChanged }) {
  const [channelId, setChannelId] = useState(channel?.id || null);
  const [connection, setConnection] = useState(null);
  const [proxy, setProxy] = useState(EMPTY_PROXY);
  const [password, setPassword] = useState('');
  const [clearPassword, setClearPassword] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const linkRef = useRef(null);

  useEffect(() => {
    if (link) linkRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [link]);

  useEffect(() => {
    if (!channel) return;
    AnalyticsService.connection(channel.id)
      .then((next) => {
        setConnection(next);
        setProxy(next.proxy || EMPTY_PROXY);
      })
      .catch(() => setError('Could not load the connection.'));
  }, [channel]);

  // Picks up the result of Google sign-in, which finishes in another tab.
  useEffect(() => {
    if (!link || !channelId) return undefined;
    const startedAt = connection?.connectedAt;
    const interval = setInterval(async () => {
      try {
        const next = await AnalyticsService.connection(channelId);
        if (next.connected && next.connectedAt !== startedAt && !next.needsReconnect) {
          setConnection(next);
          setLink('');
          setNotice(`Connected to ${next.youtubeChannelTitle}. Its full history is being pulled now; this can take a few minutes.`);
          onChanged();
        }
      } catch (_) {
        // Keep waiting; the next poll may succeed.
      }
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [link, channelId, connection?.connectedAt, onChanged]);

  async function action(work) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await work();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not complete this step.');
    } finally {
      setBusy(false);
    }
  }

  function changeProxy(key, value) {
    setProxy((current) => ({ ...current, [key]: value }));
    setDirty(true);
    setLink('');
  }

  function passwordField() {
    if (clearPassword) return { password: '' };
    return password ? { password } : {};
  }

  const saveProxy = () =>
    action(async () => {
      const proxyValues = { ...proxy, port: Number(proxy.port) };
      const { password: nextPassword } = passwordField();
      if (channelId) {
        setConnection(await AnalyticsService.saveConnection(channelId, proxyValues, nextPassword));
      } else {
        const created = await AnalyticsService.createConnectedChannel(proxyValues, nextPassword);
        setChannelId(created.id);
        setConnection(created.connection);
        onChanged();
      }
      setPassword('');
      setClearPassword(false);
      setDirty(false);
      setNotice('Proxy saved.');
    });

  const createLink = () =>
    action(async () => {
      setLink((await AnalyticsService.connectionLink(channelId)).url);
    });

  const loading = channel && !connection && !error;
  const summary = connection && channelId ? connectionSummary(connection) : null;

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{channel ? `YouTube connection · ${channel.name}` : 'Connect a YouTube channel'}</DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {!channel && (
              <DialogContentText>
                Vid Helper reads this channel's analytics and revenue through the channel's proxy.
                It only reads; it never uploads or changes anything on YouTube.
              </DialogContentText>
            )}
            {connection && !connection.configured && (
              <Alert severity="warning">
                YouTube sign-in is not configured on the server yet. Ask the Vid Helper team to set it up.
              </Alert>
            )}
            {summary && <Alert severity={summary.severity}>{summary.text}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            {notice && <Alert severity="success">{notice}</Alert>}

            <Typography variant="subtitle1">Channel proxy</Typography>
            <TextField
              select
              label="Proxy protocol"
              value={proxy.protocol}
              onChange={(event) => changeProxy('protocol', event.target.value)}
              disabled={busy}
            >
              <MenuItem value="http">HTTP</MenuItem>
              <MenuItem value="https">HTTPS</MenuItem>
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Proxy IP address"
                value={proxy.host}
                onChange={(event) => changeProxy('host', event.target.value)}
                disabled={busy}
                helperText="Public IPv4 address"
              />
              <TextField
                label="Port"
                type="number"
                sx={{ width: { xs: '100%', sm: 140 }, flexShrink: 0 }}
                value={proxy.port}
                onChange={(event) => changeProxy('port', event.target.value)}
                disabled={busy}
                inputProps={{ min: 1, max: 65535 }}
              />
            </Stack>
            <TextField
              label="Proxy username"
              value={proxy.username}
              onChange={(event) => changeProxy('username', event.target.value)}
              disabled={busy}
              autoComplete="off"
            />
            <TextField
              label="Proxy password"
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setDirty(true);
                setLink('');
              }}
              disabled={busy || clearPassword}
              autoComplete="new-password"
              placeholder={connection?.hasProxyPassword && !clearPassword ? '••••••••' : undefined}
              InputLabelProps={connection?.hasProxyPassword && !clearPassword ? { shrink: true } : undefined}
              helperText={
                connection?.hasProxyPassword
                  ? 'Enter a new password to replace the saved one.'
                  : 'Optional for proxies without authentication.'
              }
            />
            {connection?.hasProxyPassword && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={clearPassword}
                    disabled={busy}
                    onChange={(event) => {
                      setClearPassword(event.target.checked);
                      setDirty(true);
                      setLink('');
                    }}
                  />
                }
                label="Remove saved proxy password"
              />
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                onClick={saveProxy}
                disabled={busy || !proxy.host || !proxy.port || (channelId && !dirty)}
              >
                {channelId ? 'Save proxy' : 'Save proxy and continue'}
              </Button>
              {channelId && (
                <Button
                  onClick={createLink}
                  disabled={busy || dirty || !connection?.proxy || !connection?.configured}
                  sx={{ boxShadow: 'none' }}
                >
                  {connection?.connected ? 'Reconnect with Google' : 'Sign in with Google'}
                </Button>
              )}
            </Stack>
            {dirty && channelId && (
              <Typography variant="caption">Save the proxy before signing in with Google.</Typography>
            )}
            {link && (
              <Alert severity="info" icon={false} ref={linkRef}>
                Open this link in the browser that uses this channel's proxy, sign in to the Google
                account that owns the channel, choose the channel, and approve YouTube Analytics and
                revenue access. The link expires in 10 minutes; this window updates when you finish.
                <TextField
                  fullWidth
                  multiline
                  label="Sign-in link"
                  value={link}
                  margin="normal"
                  InputProps={{ readOnly: true }}
                  onFocus={(event) => event.target.select()}
                />
                <Button
                  startIcon={<ContentCopyIcon />}
                  onClick={() => navigator.clipboard.writeText(link).then(() => setNotice('Sign-in link copied.'))}
                  sx={{ boxShadow: 'none' }}
                >
                  Copy link
                </Button>
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {channelId ? 'Done' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
